import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpenFundRequestDtoIn } from '@/models/in/OpenFundRequestDtoIn';
import { OpenFundRequestStatus } from '@/models/common/OpenFundRequestStatus';
import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import { RelationshipOptions } from '@/models/common/RelationshipOptions';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import CustomDialog from '@/components/ui/Dialog/Dialog';

import styles from './OpenFundRequestEditDialog.module.scss';
import { toHebrewName } from '@/helpers/hebrewName';

const isWelfareClassification = (c: FundClassification) => c === FundClassification.Welfare || c === FundClassification.CampaignForWelfare;

const isCommunityClassification = (c: FundClassification) => c === FundClassification.Community || c === FundClassification.CampaignForCommunity;

const WELFARE_GOALS = [
  FundGoals.WidowsAndOrphans,
  FundGoals.BrideHelp,
  FundGoals.Medicine,
  FundGoals.Disasters,
  FundGoals.Debts,
  FundGoals.DifficultFinancialSituation,
  FundGoals.Other,
  FundGoals.OperationIronSwords,
];

const COMMUNITY_GOALS = [FundGoals.FamilySupport, FundGoals.KollelSupport, FundGoals.CommunityExpenses, FundGoals.Other];

const RELATIONSHIP_OPTIONS = Object.values(RelationshipOptions);

const PERSON_LABELS: Record<string, string> = {
  israeliId: 'מספר זהות',
  firstName: 'שם פרטי',
  lastName: 'שם משפחה',
  city: 'עיר',
  street: 'רחוב',
  houseNumber: 'מספר בית',
  phone: 'טלפון',
  email: 'דוא"ל',
};

const COORDINATOR_FIELDS = ['israeliId', 'firstName', 'lastName', 'city', 'street', 'houseNumber', 'phone', 'email'] as const;
const SUPPORTED_FIELDS = ['israeliId', 'firstName', 'lastName', 'city', 'street', 'houseNumber', 'email', 'phone'] as const;
const isNameField = (field: string) => field === 'firstName' || field === 'lastName';

interface OpenFundRequestEditDialogProps {
  open: boolean;
  onClose: () => void;
  request: OpenFundRequestDtoIn;
  onSaved: () => void;
}

/**
 * Edits a fund-opening request. The dialog holds *only* the values that may be changed in the request's
 * current status, so what is editable is never a guess and the read-only page behind it stays clean.
 *
 * Once the fund exists, its name and the people's details are already registered in the fund, in the
 * coordinators' users, in the supporteds and in the signed agreement - editing them would only make the
 * request diverge from reality, so from then on only the descriptive values are offered here (and the server
 * enforces the same rule).
 */
const OpenFundRequestEditDialog = ({ open, onClose, request, onSaved }: OpenFundRequestEditDialogProps) => {
  const { t } = useTranslation();

  const isAfterFundCreation = request.status === OpenFundRequestStatus.Completed;
  const classification = request.fundClassification;

  const [formData, setFormData] = useState<OpenFundRequestDtoIn>(request);
  const [notifyCoordinators, setNotifyCoordinators] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: updateRequest } = usePutAuthApi<void, { notifyCoordinators: boolean }, OpenFundRequestDtoIn>(`funds/creation/${request.requestId}`);

  useEffect(() => {
    if (open) {
      setFormData(JSON.parse(JSON.stringify(request)));
      setNotifyCoordinators(true);
      setSubmitted(false);
      setError(null);
    }
  }, [open, request]);

  const updateField = useCallback((path: string, value: unknown) => {
    setFormData((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return copy;
    });
  }, []);

  const toggleFundGoal = useCallback((goal: FundGoals) => {
    setFormData((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      if (copy.fundGoals.includes(goal)) {
        copy.fundGoals = copy.fundGoals.filter((g: FundGoals) => g !== goal);
      } else {
        copy.fundGoals.push(goal);
      }
      if (!copy.fundGoals.includes(FundGoals.Other)) {
        copy.otherGoalDescription = '';
      }
      return copy;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSubmitted(true);
    if (!formData.fundName) return;

    const hasChanges =
      JSON.stringify({ ...request, creationTime: undefined, lastModifiedTime: undefined }) !==
      JSON.stringify({ ...formData, creationTime: undefined, lastModifiedTime: undefined });

    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      // The coordinators are only notified about edits made after their fund already exists - the earlier
      // statuses have their own notifications, so there the flag is always off.
      const notify = isAfterFundCreation && notifyCoordinators;
      await updateRequest({ params: { notifyCoordinators: notify }, body: formData });
      onSaved();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const status = e?.response?.status ?? e?.status;
      const data = e?.response?.data;
      const message = data?.message ?? (typeof data === 'string' ? data : null) ?? e?.message ?? 'שגיאה לא ידועה';
      setError(status ? `שגיאה ${status}: ${message}` : message);
    } finally {
      setIsSaving(false);
    }
  }, [formData, request, isAfterFundCreation, notifyCoordinators, updateRequest, onSaved, onClose]);

  const availableGoals = isCommunityClassification(classification) ? COMMUNITY_GOALS : WELFARE_GOALS;
  const showGoals = isWelfareClassification(classification) || isCommunityClassification(classification);
  const showReasonSummary = showGoals;

  const textField = (label: string, value: string, onChange: (v: string) => void) => (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      <input className={styles.input} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );

  return (
    <CustomDialog
      open={open}
      setOpen={(value) => {
        if (!value) onClose();
      }}
      onBtnClick={handleSave}
      btnText={isSaving ? 'saving' : 'save'}
      btnDisabled={isSaving}
    >
      <div className={styles.body}>
        <h3 className={styles.title}>עריכת פרטי הבקשה</h3>

        <p className={styles.note}>
          {isAfterFundCreation ? 'הפרויקט כבר הוקם, ולכן הבקשה סגורה לשינויים. אלה הערכים היחידים שניתן לעדכן:' : 'אלה הערכים שניתן לעדכן בבקשה:'}
        </p>

        <div className={styles.fields}>
          {!isAfterFundCreation && (
            <>
              {textField('שם הפרויקט', formData.fundName, (v) => updateField('fundName', v))}
              {submitted && !formData.fundName && <div className={styles.error}>שם הפרויקט הוא שדה חובה</div>}
            </>
          )}

          {isCommunityClassification(classification) &&
            textField(formData.responsibleParty === 'charityTrustee' ? 'שם גבאי צדקה' : 'שם הקהילה', formData.responsibleEntityName || '', (v) =>
              updateField('responsibleEntityName', v)
            )}

          {showReasonSummary && (
            <label className={styles.field}>
              <span className={styles.label}>סיכום הסיבה</span>
              <textarea className={styles.input} value={formData.reasonSummary || ''} onChange={(e) => updateField('reasonSummary', e.target.value)} rows={4} />
            </label>
          )}

          {showGoals && (
            <div className={styles.field}>
              <span className={styles.label}>מטרות הפרויקט</span>
              <div className={styles.goalsRow}>
                {availableGoals.map((goal) => (
                  <label key={goal} className={styles.checkboxItem}>
                    <input type="checkbox" checked={formData.fundGoals.includes(goal)} onChange={() => toggleFundGoal(goal)} />
                    {t(`openFundRequest.fundGoals.${goal}`)}
                  </label>
                ))}
              </div>
            </div>
          )}

          {showGoals &&
            formData.fundGoals.includes(FundGoals.Other) &&
            textField('פירוט מטרת ה"אחר"', formData.otherGoalDescription || '', (v) => updateField('otherGoalDescription', v))}

          {!isAfterFundCreation && (
            <>
              <span className={styles.groupTitle}>פרטי מגיש הבקשה</span>
              {textField('שם פרטי', formData.applicantDetails.firstName, (v) => updateField('applicantDetails.firstName', v))}
              {textField('שם משפחה', formData.applicantDetails.lastName, (v) => updateField('applicantDetails.lastName', v))}
              {textField('דואר אלקטרוני', formData.applicantDetails.email, (v) => updateField('applicantDetails.email', v))}

              {formData.coordinators.map((coordinator, i) => (
                <div key={i} className={styles.group}>
                  <span className={styles.groupTitle}>{formData.coordinators.length > 1 ? `רכז ${i + 1}` : 'רכז הפרויקט'}</span>
                  {COORDINATOR_FIELDS.map((field) => (
                    <div key={field}>{textField(PERSON_LABELS[field], coordinator[field], (v) => updateField(`coordinators.${i}.${field}`, isNameField(field) ? toHebrewName(v) : v))}</div>
                  ))}
                </div>
              ))}

              {isWelfareClassification(classification) &&
                formData.supporteds.map((supported, i) => (
                  <div key={i} className={styles.group}>
                    <span className={styles.groupTitle}>{formData.supporteds.length > 1 ? `נתמך ${i + 1}` : 'הנתמך'}</span>
                    {SUPPORTED_FIELDS.map((field) => (
                      <div key={field}>{textField(PERSON_LABELS[field], supported[field], (v) => updateField(`supporteds.${i}.${field}`, isNameField(field) ? toHebrewName(v) : v))}</div>
                    ))}
                    {/* The first supported is always "self" - that relation is a fact, not a choice */}
                    {supported.relationshipToFirstSupported !== RelationshipOptions.Self && (
                      <label className={styles.field}>
                        <span className={styles.label}>הקשר המשפחתי לנתמך הראשון</span>
                        <select
                          className={styles.input}
                          value={supported.relationshipToFirstSupported}
                          onChange={(e) => updateField(`supporteds.${i}.relationshipToFirstSupported`, e.target.value)}
                        >
                          <option value="" disabled>
                            בחר קשר משפחתי
                          </option>
                          {RELATIONSHIP_OPTIONS.map((option) => (
                            <option key={option} value={option} disabled={option === RelationshipOptions.Self}>
                              {t(`openFundRequest.relationships.${option}`)}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                ))}
            </>
          )}

          <div className={styles.group}>
            <label className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={formData.personalInterfaceInNedarimPlus}
                onChange={() => updateField('personalInterfaceInNedarimPlus', !formData.personalInterfaceInNedarimPlus)}
              />
              מעוניינים בממשק אישי בנדרים פלוס
            </label>
            <label className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={formData.crowdfundingCampaign}
                onChange={() => updateField('crowdfundingCampaign', !formData.crowdfundingCampaign)}
              />
              מעוניינים בהפעלת קמפיין למימון המונים
            </label>
          </div>
        </div>

        {isAfterFundCreation && (
          <label className={styles.notifyRow}>
            <input type="checkbox" checked={notifyCoordinators} onChange={() => setNotifyCoordinators((prev) => !prev)} />
            לשלוח לרכזי הפרויקט עדכון במייל על השינויים
          </label>
        )}

        {error && <div className={styles.error}>{error}</div>}
      </div>
    </CustomDialog>
  );
};

export default OpenFundRequestEditDialog;
